import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiAuthGooglePost } from '@/entities/auth';
import { notifyAuthChanged } from '@/entities/auth';
import {
  getOrCreateDeviceId,
  setStoredUser,
  setTokens,
} from '@/shared/lib/auth';
import {
  isAppleBridgeLoginAvailable,
  isInFlutterWebView,
  requestFlutterAppleLogin,
  requestFlutterGoogleLogin,
  subscribeToFlutterAuth,
  type BridgeAuthResult,
} from '@/shared/lib/bridge';
import { connectRealtime } from '@/shared/lib/realtime';
import { extractErrorMessage } from '@/shared/api/errors';
import { showToast } from '@/shared/hooks/useToast';
import i18n from '@/shared/lib/i18n';

type LoginState = {
  isLoading: boolean;
  error: string | null;
};

export const useLogin = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<LoginState>({ isLoading: false, error: null });
  const inFlutterRef = useRef(isInFlutterWebView());
  const appleAvailableRef = useRef(isAppleBridgeLoginAvailable());

  const completeLogin = useCallback(
    (payload: BridgeAuthResult) => {
      setTokens(payload.access_token, payload.refresh_token);
      if (payload.user) {
        setStoredUser({
          id: payload.user.id,
          email: payload.user.email,
          full_name: payload.user.full_name,
          avatar_url: payload.user.avatar_url ?? null,
        });
      }
      notifyAuthChanged();
      // Re-open the realtime socket for the freshly-signed-in user.
      // Logout calls `disconnectRealtime` (which nulls the singleton), so
      // `connectRealtime` here always creates a fresh connection bound to
      // the new token. No-op if `RealtimeProvider` is still holding one.
      connectRealtime(qc);
      navigate('/parcels', { replace: true });
    },
    [navigate, qc],
  );

  useEffect(() => {
    if (!inFlutterRef.current) return;
    const unsubscribe = subscribeToFlutterAuth(
      (payload) => {
        setState({ isLoading: false, error: null });
        completeLogin(payload);
        showToast({ kind: 'success', message: i18n.t('common:loginSuccess') });
      },
      (message) => {
        setState({ isLoading: false, error: message });
        showToast({ kind: 'error', message });
      },
    );
    return unsubscribe;
  }, [completeLogin]);

  const loginWithGoogleIdToken = useCallback(
    async (idToken: string) => {
      setState({ isLoading: true, error: null });
      try {
        const response = await apiAuthGooglePost({
          id_token: idToken,
          device_id: getOrCreateDeviceId(),
        });
        completeLogin(response);
        showToast({ kind: 'success', message: i18n.t('common:loginSuccess') });
      } catch (err) {
        const message = extractErrorMessage(err, i18n.t('common:loginError'));
        setState({ isLoading: false, error: message });
        showToast({ kind: 'error', message });
      }
    },
    [completeLogin],
  );

  const requestNativeLogin = useCallback((): boolean => {
    if (!inFlutterRef.current) return false;
    setState({ isLoading: true, error: null });
    const ok = requestFlutterGoogleLogin();
    if (!ok) {
      setState({ isLoading: false, error: i18n.t('common:flutterBridgeMissing') });
    }
    return ok;
  }, []);

  const requestNativeAppleLogin = useCallback((): boolean => {
    if (!appleAvailableRef.current) return false;
    setState({ isLoading: true, error: null });
    // The native side performs Apple sign-in + POST /auth/apple and calls
    // back through the SAME window.onJapanCargoAuth handler that Google uses,
    // so the existing subscribeToFlutterAuth effect completes the login.
    const ok = requestFlutterAppleLogin();
    if (!ok) {
      setState({ isLoading: false, error: i18n.t('common:flutterBridgeMissing') });
    }
    return ok;
  }, []);

  const loginWithTokens = useCallback(
    (access: string, refresh: string) => {
      completeLogin({ access_token: access, refresh_token: refresh });
      showToast({ kind: 'success', message: i18n.t('common:tokensSaved') });
    },
    [completeLogin],
  );

  return {
    ...state,
    isInFlutter: inFlutterRef.current,
    isAppleAvailable: appleAvailableRef.current,
    loginWithGoogleIdToken,
    loginWithTokens,
    requestNativeLogin,
    requestNativeAppleLogin,
  };
};
